import { memo, useMemo } from "react";
import { rgbToCss } from "utils/rgbToCss";
import { ROUTE_STROKE_WIDTH } from "../CircuitPathsLayer/constants";
import { getCircleSize } from "../utils";
import {
  ACTIVE_BLOOM_OPACITY,
  ACTIVE_BLOOM_STROKE_WIDTH,
  CREATION_RGB,
  NODE_RADIUS_FACTOR,
  SEGMENT_HIT_STROKE_WIDTH,
  SELECTED_NODE_HALO_FACTOR,
  SELECTED_NODE_HALO_STROKE,
} from "./constants";
import { nodesEdgesToSvgPath } from "./helpers/nodesEdgesToSvgPath";
import type {
  ActiveRouteRenderProps,
  RouteCreationOverlayEdge,
  RouteCreationOverlayNode,
} from "./types";

export const ActiveRouteRender = memo(function ActiveRouteRender({
  route,
  isEditable,
  selectedNodeId,
  zoomLevel,
  circleSizeScale,
  onNodeMouseDown,
  onSegmentMouseDown,
}: ActiveRouteRenderProps) {
  const color = rgbToCss(CREATION_RGB);
  const size = getCircleSize(zoomLevel) * circleSizeScale;
  const nodeRadius = (size / 2) * NODE_RADIUS_FACTOR;
  const selectedHaloRadius = nodeRadius * SELECTED_NODE_HALO_FACTOR;

  const nodeById = useMemo(
    () =>
      new Map<number, RouteCreationOverlayNode>(
        route.nodes.map((node) => [node.id, node]),
      ),
    [route.nodes],
  );

  const renderableEdges = useMemo(
    () =>
      route.edges
        .map((edge, index) => {
          const fromNode = nodeById.get(edge.from);
          const toNode = nodeById.get(edge.to);
          if (!fromNode || !toNode) return null;
          return { edge, fromNode, toNode, index };
        })
        .filter(
          (
            entry,
          ): entry is {
            edge: RouteCreationOverlayEdge;
            fromNode: RouteCreationOverlayNode;
            toNode: RouteCreationOverlayNode;
            index: number;
          } => entry !== null,
        ),
    [route.edges, nodeById],
  );

  const pathD = useMemo(
    () => nodesEdgesToSvgPath({ nodes: route.nodes, edges: route.edges }),
    [route.nodes, route.edges],
  );

  return (
    <g>
      {pathD && (
        <>
          <path
            d={pathD}
            stroke={color}
            strokeWidth={ACTIVE_BLOOM_STROKE_WIDTH}
            strokeOpacity={ACTIVE_BLOOM_OPACITY}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          <path
            d={pathD}
            stroke={color}
            strokeWidth={ROUTE_STROKE_WIDTH}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </>
      )}
      {isEditable &&
        renderableEdges.map(({ fromNode, toNode, index }) => (
          <line
            key={`edge-hit-${index}`}
            x1={fromNode.x}
            y1={fromNode.y}
            x2={toNode.x}
            y2={toNode.y}
            stroke="transparent"
            strokeWidth={SEGMENT_HIT_STROKE_WIDTH}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            style={{ pointerEvents: "all", cursor: "copy" }}
            onMouseDown={(event) =>
              onSegmentMouseDown?.({
                routeId: route.id,
                edgeIndex: index,
                event,
              })
            }
          />
        ))}
      {route.nodes.map((node) => {
        const isSelected = isEditable && selectedNodeId === node.id;
        return (
          <g key={`node-${node.id}`}>
            {isSelected && (
              <circle
                cx={node.x}
                cy={node.y}
                r={selectedHaloRadius}
                fill="none"
                stroke={SELECTED_NODE_HALO_STROKE}
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
              />
            )}
            <circle
              cx={node.x}
              cy={node.y}
              r={nodeRadius}
              fill={color}
              fillOpacity={0.85}
              stroke={color}
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
              style={
                isEditable
                  ? { pointerEvents: "all", cursor: "grab" }
                  : undefined
              }
              onMouseDown={
                isEditable
                  ? (event) =>
                      onNodeMouseDown?.({
                        routeId: route.id,
                        nodeId: node.id,
                        event,
                      })
                  : undefined
              }
            />
          </g>
        );
      })}
    </g>
  );
});
